import { Routes, Route, Navigate } from "react-router-dom";

import { isMobileDevice } from "../../common/utilities/platform/platform.util";
import UnverifiedProtectedRoute from "./firebase/unverified-protected-route.comp";
import VerifiedProtectedRoute from "./firebase/verified-protected-route.comp";
import AdminProtectedRoute from "./firebase/admin-protected-route.comp";
import VerifiedSignInRoute from "../routes/verified-signin-route.comp";
import GeneralMenuLayout from "../layout/general-menu-layout.comp";
import FirebaseSignIn from "../../views/auth/signin/signin";
import FirebaseSignUp from "../../views/auth/signup/signup";
import TrialFirebaseSignUp from "../../views/auth/signup/signup-trial";
import ForgotPassword from "../../views/auth/forgot-password/forgot-password";
import EmaiConfirm from "../../views/auth/email-confirm/email-confirm";
import EmaiConfirmed from "../../views/auth/email-confirmed/email-confirmed";
import ChatBot from "../../views/chat-bot/chat-bot";
import NotFound from "../../views/not-found/not-found";
import ErrorPage from "../../views/error-page/error-page";
import KnowledgeBaseElements from "../../views/admin/knowledge-base-elements/knowledge-base-elements";
import RecognitionResultView from "../../views/admin/recognition-result/recognition-result";
import CompareRecognitionResultView from "../../views/admin/compare-recognition-result/compare-recognition-result";
import UploadedFilesView from "../../views/admin/uploaded-files/uploaded-files";
import PromptTuningView from "../../views/admin/prompt-tuning/prompt-tuning";
import AssistantSettingsView from "../../views/admin/assistant-settings/assistant-settings";
import TrialKnowledgeBaseView from "../../views/admin/trial-knowledge-base/trial-knowledge-base";
import TrialHomeView from "../../views/admin/trial-home/trial-home";
import InviteCodesView from "../../views/admin/invite-codes/invite-codes";
import SwitchScreenView from "../../views/admin/switch-screen/switch-screen";
import DashboardView from "../../views/admin/dashboard/dashboard";
import CustomizationView from "../../views/customization/customization";

export const EmptyRoutes = () => {
    return (
        <Routes>
            <Route path="*" element={<></>} />
        </Routes>
    );
};

enum RouteType {
    default,
    trial
}

const AssistantRoutes = (type: RouteType) => {
    return (
        <Routes>
            {type === RouteType.trial ? (
                <Route
                    path="/registration"
                    element={
                        <VerifiedSignInRoute>
                            <TrialFirebaseSignUp />
                        </VerifiedSignInRoute>
                    }
                />
            ) : (
                <Route
                    path="/registration"
                    element={
                        <VerifiedSignInRoute>
                            <FirebaseSignUp />
                        </VerifiedSignInRoute>
                    }
                />
            )}
            <Route path="/" element={<Navigate to="/new-chat" />} />
            <Route
                path="/new-chat"
                element={
                    <VerifiedProtectedRoute>
                        <ChatBot isNewChat={true} />
                    </VerifiedProtectedRoute>
                }
            />
            <Route
                path="/chat/:chatId"
                element={
                    <VerifiedProtectedRoute>
                        <ChatBot />
                    </VerifiedProtectedRoute>
                }
            />
            <Route
                path="/login"
                element={
                    <VerifiedSignInRoute>
                        <FirebaseSignIn />
                    </VerifiedSignInRoute>
                }
            />
            <Route
                path="/forgot-password"
                element={
                    <VerifiedSignInRoute>
                        <ForgotPassword />
                    </VerifiedSignInRoute>
                }
            />
            <Route
                path="/email-confirm"
                element={
                    <UnverifiedProtectedRoute>
                        <EmaiConfirm />
                    </UnverifiedProtectedRoute>
                }
            />
            <Route
                path="/registration-success"
                element={
                    <VerifiedProtectedRoute>
                        <EmaiConfirmed />
                    </VerifiedProtectedRoute>
                }
            />
            <Route path="/error" element={<ErrorPage />} />
            <Route path="*" element={<NotFound />} />
        </Routes>
    );
};

export const DefaultAssistantRoutes = () => {
    return AssistantRoutes(RouteType.default);
};

export const TrialAssistantRoutes = () => {
    return AssistantRoutes(RouteType.trial);
};

const AdminRoutes = (type: RouteType) => {
    return (
        <Routes>
            {!isMobileDevice() ? (
                <>
                    {type === RouteType.trial ? (
                        <>
                            <Route
                                path="/"
                                element={
                                    <VerifiedProtectedRoute>
                                        <GeneralMenuLayout>
                                            <TrialHomeView />
                                        </GeneralMenuLayout>
                                    </VerifiedProtectedRoute>
                                }
                            />
                            <Route
                                path="/knowledge-base"
                                element={
                                    <VerifiedProtectedRoute>
                                        <GeneralMenuLayout>
                                            <TrialKnowledgeBaseView />
                                        </GeneralMenuLayout>
                                    </VerifiedProtectedRoute>
                                }
                            />
                            <Route
                                path="/knowledge-base/:providerId"
                                element={<Navigate to="/knowledge-base" />}
                            />
                            <Route
                                path="/knowledge-base/:providerId/:courseId"
                                element={<Navigate to="/knowledge-base" />}
                            />
                        </>
                    ) : (
                        <>
                            <Route
                                path="/"
                                element={
                                    <VerifiedProtectedRoute>
                                        <GeneralMenuLayout>
                                            <DashboardView />
                                        </GeneralMenuLayout>
                                    </VerifiedProtectedRoute>
                                }
                            />
                            <Route
                                path="/knowledge-base"
                                element={
                                    <VerifiedProtectedRoute>
                                        <GeneralMenuLayout>
                                            <KnowledgeBaseElements />
                                        </GeneralMenuLayout>
                                    </VerifiedProtectedRoute>
                                }
                            />
                            <Route
                                path="/knowledge-base/:providerId"
                                element={
                                    <VerifiedProtectedRoute>
                                        <GeneralMenuLayout>
                                            <KnowledgeBaseElements />
                                        </GeneralMenuLayout>
                                    </VerifiedProtectedRoute>
                                }
                            />
                            <Route
                                path="/knowledge-base/:providerId/:courseId"
                                element={
                                    <VerifiedProtectedRoute>
                                        <GeneralMenuLayout>
                                            <KnowledgeBaseElements />
                                        </GeneralMenuLayout>
                                    </VerifiedProtectedRoute>
                                }
                            />
                            <Route
                                path="/compare-recognition-result/:providerId/:uid"
                                element={
                                    <VerifiedProtectedRoute>
                                        <GeneralMenuLayout>
                                            <CompareRecognitionResultView />
                                        </GeneralMenuLayout>
                                    </VerifiedProtectedRoute>
                                }
                            />
                            <Route
                                path="/uploaded-files"
                                element={
                                    <VerifiedProtectedRoute>
                                        <GeneralMenuLayout>
                                            <UploadedFilesView />
                                        </GeneralMenuLayout>
                                    </VerifiedProtectedRoute>
                                }
                            />
                        </>
                    )}
                    <Route
                        path="/prompt-tuning"
                        element={
                            <VerifiedProtectedRoute>
                                <AdminProtectedRoute>
                                    <GeneralMenuLayout>
                                        <PromptTuningView />
                                    </GeneralMenuLayout>
                                </AdminProtectedRoute>
                            </VerifiedProtectedRoute>
                        }
                    />
                    <Route
                        path="/assistant-settings"
                        element={
                            <VerifiedProtectedRoute>
                                <GeneralMenuLayout>
                                    <AssistantSettingsView />
                                </GeneralMenuLayout>
                            </VerifiedProtectedRoute>
                        }
                    />
                    <Route
                        path="/recognition-result/:providerId/:uid"
                        element={
                            <VerifiedProtectedRoute>
                                <GeneralMenuLayout>
                                    <RecognitionResultView />
                                </GeneralMenuLayout>
                            </VerifiedProtectedRoute>
                        }
                    />
                    <Route
                        path="/manage-invites"
                        element={
                            <VerifiedProtectedRoute>
                                <GeneralMenuLayout>
                                    <InviteCodesView />
                                </GeneralMenuLayout>
                            </VerifiedProtectedRoute>
                        }
                    />
                    <Route
                        path="/customization"
                        element={
                            <VerifiedProtectedRoute>
                                <GeneralMenuLayout>
                                    <CustomizationView />
                                </GeneralMenuLayout>
                            </VerifiedProtectedRoute>
                        }
                    />
                    <Route path="*" element={<NotFound />} />
                </>
            ) : (
                <>
                    <Route
                        path="*"
                        element={
                            <VerifiedProtectedRoute>
                                <GeneralMenuLayout>
                                    <SwitchScreenView />
                                </GeneralMenuLayout>
                            </VerifiedProtectedRoute>
                        }
                    />
                </>
            )}
            {type === RouteType.trial ? (
                <Route
                    path="/registration"
                    element={
                        <VerifiedSignInRoute>
                            <TrialFirebaseSignUp />
                        </VerifiedSignInRoute>
                    }
                />
            ) : (
                <Route
                    path="/registration"
                    element={
                        <VerifiedSignInRoute>
                            <FirebaseSignUp />
                        </VerifiedSignInRoute>
                    }
                />
            )}
            <Route
                path="/email-confirm"
                element={
                    <UnverifiedProtectedRoute>
                        <EmaiConfirm />
                    </UnverifiedProtectedRoute>
                }
            />
            <Route
                path="/login"
                element={
                    <VerifiedSignInRoute>
                        <FirebaseSignIn />
                    </VerifiedSignInRoute>
                }
            />
            <Route
                path="/forgot-password"
                element={
                    <VerifiedSignInRoute>
                        <ForgotPassword />
                    </VerifiedSignInRoute>
                }
            />
            <Route
                path="/registration-success"
                element={
                    <VerifiedProtectedRoute>
                        <EmaiConfirmed />
                    </VerifiedProtectedRoute>
                }
            />
            <Route path="/error" element={<ErrorPage />} />
        </Routes>
    );
};

export const DefaultAdminRoutes = () => {
    return AdminRoutes(RouteType.default);
};

export const TrialAdminRoutes = () => {
    return AdminRoutes(RouteType.trial);
};
